import {
  Button,
  Field,
  Input,
  Text,
} from "@fluentui/react-components";
import {
  Add16Regular,
  ArrowDown16Regular,
  ArrowUp16Regular,
  Dismiss16Regular,
} from "@fluentui/react-icons";
import { OverflowTooltip } from "../../../externalDiagnostics/OverflowTooltip/OverflowTooltip";
import { LabelWithInfo } from "../../labelWithInfo/LabelWithInfo";
import { ILikertScaleFormProps } from "./editObjective.types";

export const LikertScaleForm = (props: ILikertScaleFormProps): JSX.Element => {
  const scaleLabelEntries = Object.entries(props.likertScaleLabels);

  return (
    <>
      {props.fieldErrors.statements && (
        <div
          role="alert"
          aria-live="assertive"
          className={props.dialogContentStyles.feedbackError}
        >
          {props.fieldErrors.statements}
        </div>
      )}
      <div className={props.editStyles.questionSectionWrapper}>
        <Field
          label={
            <LabelWithInfo
              label="Statements"
              info="These statements will be asked exactly as written during the interview. Add up to 10 statements."
              required
            />
          }
        />
        {props.likertStatements.map((stmt, idx) => (
          <div key={idx} className={props.editStyles.reorderableRow}>
            <OverflowTooltip text={stmt.text} overflowSelector="input">
              <Input
                value={stmt.text}
                placeholder="Enter a statement label"
                disabled={props.isLoading}
                style={{ width: "100%" }}
                onChange={(_, data) =>
                  props.onUpdateStatement(idx, { text: data.value })
                }
              />
            </OverflowTooltip>
            <Button
              icon={<ArrowUp16Regular />}
              appearance="subtle"
              disabled={props.isLoading || idx === 0}
              onClick={() => props.onMoveStatement(idx, -1)}
              aria-label={`Move statement ${idx + 1} up`}
            />
            <Button
              icon={<ArrowDown16Regular />}
              appearance="subtle"
              disabled={
                props.isLoading || idx === props.likertStatements.length - 1
              }
              onClick={() => props.onMoveStatement(idx, 1)}
              aria-label={`Move statement ${idx + 1} down`}
            />
            <Button
              icon={<Dismiss16Regular />}
              appearance="subtle"
              disabled={props.isLoading}
              onClick={() => props.onRemoveStatement(idx)}
              aria-label={`Remove statement ${idx + 1}`}
            />
          </div>
        ))}
     
        <Button
          icon={<Add16Regular />}
          appearance="secondary"
          onClick={props.onAddStatement}
          disabled={
            props.isLoading ||
            props.likertStatements.length >= props.maxStatementsCount
          }
          className={props.editStyles.addAnswerButton}
        >
          Add Statement
        </Button>
      </div>
      <div className={props.editStyles.questionSectionWrapper}>
        <Field
          label={
            <LabelWithInfo
              label="Scale Labels"
              info="Customize the agreement scale labels."
              required
            />
          }
        />
        {props.fieldErrors.scaleLabels && (
          <div
            role="alert"
            aria-live="assertive"
            className={props.dialogContentStyles.feedbackError}
          >
            {props.fieldErrors.scaleLabels}
          </div>
        )}
        {scaleLabelEntries.map(([labelIndex, label], position) => (
          <div key={labelIndex} className={props.editStyles.scaleLabelRow}>
            <Text className={props.editStyles.scaleLabelNumber}>
              {labelIndex}
            </Text>
            <OverflowTooltip text={label} overflowSelector="input">
              <Input
                value={label}
                disabled={props.isLoading}
                placeholder="Enter a scale label"
                style={{ width: "100%" }}
                onChange={(_, data) => {
                  props.onUpdateScaleLabel(labelIndex, data.value);
                }}
              />
            </OverflowTooltip>
            <Button
              icon={<ArrowUp16Regular />}
              appearance="subtle"
              disabled={props.isLoading || position === 0}
              onClick={() => props.onMoveScaleLabel(labelIndex, -1)}
              aria-label={`Move scale label ${labelIndex} up`}
            />
            <Button
              icon={<ArrowDown16Regular />}
              appearance="subtle"
              disabled={
                props.isLoading || position === scaleLabelEntries.length - 1
              }
              onClick={() => props.onMoveScaleLabel(labelIndex, 1)}
              aria-label={`Move scale label ${labelIndex} down`}
            />
          </div>
        ))}
        <div className={props.editStyles.scaleLabelRow}>
          <Text className={props.editStyles.scaleLabelNumber}>0</Text>
          <Input value="N/A" disabled placeholder="N/A" />
        </div>
      </div>
    </>
  );
};
